import Katex from '@/utils/Katex.js';
import 'katex/dist/katex.css';

const EReg = {
	// 保护 LaTeX 数学块，替换为占位符，防止其中的特殊字符被后续正则误处理
	// 返回 { result, blocks }，还原时调用 restoreLatexBlocks
	_protectLatexBlocks(str) {
		const blocks = [];
		let result = str;

		// 1a. $$...$$ 行间公式（必须在 $...$ 之前处理）
		result = result.replace(/\$\$([\s\S]*?)\$\$/g, (match) => {
			blocks.push(match);
			return `\x00LATEX_${blocks.length - 1}\x00`;
		});

		// 1b. \[...\] 行间公式
		result = result.replace(/\\\[([\s\S]*?)\\\]/g, (match) => {
			blocks.push(match);
			return `\x00LATEX_${blocks.length - 1}\x00`;
		});

		// 1c. $...$ 行内公式（不匹配 $$）
		result = result.replace(/(?<!\$)\$([^$\n\r]+)\$(?!\$)/g, (match) => {
			blocks.push(match);
			return `\x00LATEX_${blocks.length - 1}\x00`;
		});

		// 1d. \(...\) 行内公式
		result = result.replace(/\\\(([\s\S]*?)\\\)/g, (match) => {
			blocks.push(match);
			return `\x00LATEX_${blocks.length - 1}\x00`;
		});

		return { result, blocks };
	},

	// 还原 LaTeX 数学块
	_restoreLatexBlocks(result, blocks) {
		blocks.forEach((block, i) => {
			// 使用函数形式避免 block 中的 $ 被 String.replace 当作特殊替换模式（如 $$→$）
			result = result.replace(`\x00LATEX_${i}\x00`, () => block);
		});
		return result;
	},

	// 转义数字范围中的 ~，防止被 markdown 解析为下标语法 ~text~
	// 如  >36~60mm → >36～60mm，200℃~250℃ → 200℃～250℃，0.5h~1h → 0.5h～1h
	// ~ 前后有空格也会一并合并，如 >16 ~ 36mm → >16～36mm
	// 会先保护 LaTeX 表达式中的 ~ 不被替换
	escapeNumberRangeTilde(str) {
		if (!str) return str;

		const { result: protectedStr, blocks } = EReg._protectLatexBlocks(str);
		let result = protectedStr.replace(/(\d[^\s~、,，;；)）\x00]*)\s*~\s*([^\s~、,，;；)）\x00]*\d)/g, '$1～$2');
		return EReg._restoreLatexBlocks(result, blocks);
	},

	// 转义数字范围中的 ^，防止被 markdown 解析为上标语法 ^text^
	// 如  10^6 → 10＾6，2^10 → 2＾10
	// 会先保护 LaTeX 表达式中的 ^ 不被替换
	escapeSuperscriptCaret(str) {
		if (!str) return str;

		const { result: protectedStr, blocks } = EReg._protectLatexBlocks(str);
		let result = protectedStr.replace(/(\d[^\s^、,，;；)）\x00]*)\s*\^\s*([^\s^、,，;；)）\x00]*\d)/g, '$1＾$2');
		return EReg._restoreLatexBlocks(result, blocks);
	},

	// 宽松引用块清洗与链接生成：模型未按 %[% %]% 规则输出时的兜底（如 [ 数据来源：章节名|文档块ID: id %] 等畸形形式）
	// split |，含「数据来源：」的段去掉前缀作章节名，含长数字串的段提取纯数字作章节 id；
	// id 非纯数字串时降级为《章节名》纯文本
	_formatLooseRefBlock(raw) {
		// 去边界符号（[ [% %[ 【 与 ] %] ]% 】）
		const inner = raw.replace(/^[%\[\【\s]+/, '').replace(/[%\]\】\s]+$/, '');
		const parts = inner.split('|').map(p => p.trim()).filter(p => p);
		let chapterName = '';
		let chapterId = '';
		parts.forEach(p => {
			const numMatch = p.match(/\d{17,18}/);
			if (numMatch) {
				// 数字段：提取纯数字串作为章节 id（顺带去掉「文档块ID:」等前缀）
				chapterId = numMatch[0];
				return;
			}
			// 含「数据来源」的段为章节名段，去掉前缀；否则第一段兜底当章节名
			if (p.indexOf('数据来源') !== -1) {
				chapterName = p.replace(/^[【\[（(]?\s*/, '').replace(/^数据来源\s*[:：]?\s*/, '').trim();
			} else if (!chapterName) {
				chapterName = p;
			}
		});
		if (!chapterName) chapterName = inner;
		// 兜底：id 非纯数字串 → 降级为《章节名》纯文本
		if (!/^\d{17,18}$/.test(chapterId)) {
			return `《${chapterName}》`;
		}
		// 与严格规则 2 段式一致：展示章节名，wd 携带章节 id 跳转
		return `<a class="ai-link" href="/StandardsPdf?wd=${encodeURIComponent(chapterId)}" target="_blank" >《${chapterName}》</a>`;
	},

	//ai-格式化跳转内容
	aiFormatContentWithLinks(str) {
		// 先转义数字范围中的 ~，防止被 markdown 解析为下标（如 >36~60mm → >36\~60mm）
		str = EReg.escapeNumberRangeTilde(str);
		// 再转义数值上下文中的 ^，防止被 markdown 解析为上标（保护 LaTeX 块不处理）
		str = EReg.escapeSuperscriptCaret(str);
		// 1. 优先匹配 %[% 和 %]% 之间的内容（严格规则，优先级最高）
		let result = str.replace(/%\[%(.*?)%\]%/g, (match, keyword) => {
			// 空内容块：直接清除（如 %[% %]%），保证页面看不到裸符号
			if (!keyword.trim()) {
				return '';
			}
			// 处理 | 分割的字符串
			const parts = keyword.split('|');
			//如果没有|，则直接返回
			if (!parts || parts.length < 2) {
				return `《${keyword}》`;
			}
			// 章节名段清洗：去掉「数据来源：」等前缀
			const chapterName = (parts[0] || keyword).replace(/^[【\[（(]?\s*数据来源\s*[:：]?\s*/, '').trim() || keyword;
			// 1个|：现有逻辑不变 → StandardsPdf
			if (parts.length === 2) {
				const searchQuery = parts[1] || parts[0] || keyword;
				// 规则5兜底：第二段若是「前缀+长数字串」形态（如 文档块ID: 153169485078090241），提取纯数字作为章节 id
				const numMatch = searchQuery.match(/\d{17,18}/);
				if (numMatch) {
					return `<a class="ai-link" href="/StandardsPdf?wd=${encodeURIComponent(numMatch[0])}" target="_blank" >《${chapterName}》</a>`;
				}
				// 含引用前缀（文档块ID等）却没有合法数字 → 降级为《章节名》纯文本
				if (/文档块|数据来源|ID/i.test(searchQuery)) {
					return `《${chapterName}》`;
				}
				return `<a class="ai-link" href="/StandardsPdf?wd=${encodeURIComponent(searchQuery)}" target="_blank" >《${chapterName}》</a>`;
			}
			// 不止3个|（>=4个|）：跳转 KnowledgeDet
			// type=最后一个值，id=第2个值，page=第3个值
			if (parts.length >= 3) {
				const typeNum = parts[1];
				const id = parts[2];
				const page = parts[parts.length - 1];
				const fileName = parts[0];
				//判断fileName 是否包含 .xls或者 .xlsx, 如果包含，则后面是第几行，否则沿用现在逻辑
				let displayText = fileName.includes('.xls') || fileName.includes('.xlsx') ? fileName + '&nbsp;&nbsp;第' + page + '行' : fileName + '&nbsp;&nbsp;第' + page + '页';
				const type = typeNum == 1 ? 'company' : 'personal';
				return `<a class="ai-link-book" href="/KnowledgeDet?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}&page=${encodeURIComponent(page)}" target="_blank">《${displayText}》</a>`;
			}
			// 其他情况（2-3个|）：降级为普通文本
			return `《${keyword}》`;
		});

		// 2. 保护已生成的 ai-link / ai-link-book 标签，防止宽松匹配误抓 href 中的数字 id
		const protectedLinks = [];
		result = result.replace(/<a class="ai-link[^"]*"[^>]*>[\s\S]*?<\/a>/g, m => {
			protectedLinks.push(m);
			return `\x00AI_LINK_${protectedLinks.length - 1}\x00`;
		});

		// 3. 宽松匹配：模型未按规则输出时的兜底——先找 17~18 位纯数字串，
		//    向前回溯最近的 [ / %[ / [% / 【 ，向后找最近的 ] / %] / ]% / 】 ，整段作为引用链接
		const numReg = /\d{17,18}/g;
		const looseRanges = [];
		let m;
		while ((m = numReg.exec(result)) !== null) {
			const numStart = m.index;
			const numEnd = numStart + m[0].length;
			// 向前找最近的起始符
			let startIdx = -1;
			['[%', '%[', '[', '【'].forEach(tag => {
				const idx = result.lastIndexOf(tag, numStart - 1);
				if (idx > startIdx) startIdx = idx;
			});
			if (startIdx === -1) continue;
			// 严格块闭合中保护：%[% ... 形态（起始为 %[%）等待严格正则完整闭合，宽松匹配不提前消费，
			// 否则会残留前导 % 导致后续尾 % 到达后无法闭合（流式分帧场景）
			if (result.slice(startIdx, startIdx + 2) === '[%' && result[startIdx - 1] === '%') continue;
			// 向后找最近的结束符
			let endIdx = -1;
			[']%', '%]', ']', '】'].forEach(tag => {
				const idx = result.indexOf(tag, numEnd);
				if (idx !== -1 && (endIdx === -1 || idx < endIdx)) endIdx = idx;
			});
			if (endIdx === -1) continue;
			// 结束符长度：]% / %] 为 2 字符，] / 】 为 1 字符
			const endTagLen = result.startsWith(']%', endIdx) || result.startsWith('%]', endIdx) ? 2 : 1;
			const blockEnd = endIdx + endTagLen;
			// markdown 链接保护：[文本](url) 形态中 ] 后紧跟 ( ，不作为引用块，避免破坏链接
			if (result.charAt(blockEnd) === '(') continue;
			looseRanges.push({ startIdx, blockEnd, raw: result.slice(startIdx, blockEnd) });
			numReg.lastIndex = blockEnd;
		}
		// 从后往前替换，避免索引偏移
		for (let i = looseRanges.length - 1; i >= 0; i--) {
			const { startIdx, blockEnd, raw } = looseRanges[i];
			result = result.slice(0, startIdx) + EReg._formatLooseRefBlock(raw) + result.slice(blockEnd);
		}

		// 4. 残留清除：以上都未匹配的裸符号直接移除，保证页面看不到；
		//    流式输出中末尾未闭合的起始符暂保留，等待后续内容闭合后再处理；
		//    只有完整结束符 %]% 才算闭合，%]（差最后一个 %）仍视为闭合中、保留尾部
		let tailIdx = -1;
		['%[%', '[%', '%['].forEach(tag => {
			const idx = result.lastIndexOf(tag);
			if (idx > tailIdx) tailIdx = idx;
		});
		const tailSegment = tailIdx !== -1 && !/%\]%/.test(result.slice(tailIdx)) ? result.slice(tailIdx) : '';
		const cleanTarget = tailSegment ? result.slice(0, tailIdx) : result;
		result = cleanTarget.replace(/%\[%|%\]%|\[%|%\[|%\]|\]%/g, '') + tailSegment;

		// 4b. 清理引用序列前后多余 %：模型偶发把引用输出为 % <章节名> % / %《章节名》% / %\n%《章节名》\n% 等形态，
		//     引用序列可包含多个连续引用（如 %《A》《B》%）；先把仅隔空白的连续 % 合并为单个，
		//     再去掉引用序列两侧的 %，最后清理引用序列后残留的孤 %（流式分帧提前清理后的尾 %）
		result = result.replace(/%\s+%/g, '%');
		const refSeq = '(?:<[^<>]+>|《[^《》]+》)(?:\\s*(?:<[^<>]+>|《[^《》]+》))*';
		result = result.replace(new RegExp('%+\\s*(' + refSeq + ')\\s*%+', 'g'), '$1');
		result = result.replace(new RegExp('(' + refSeq + ')\\s*%+', 'g'), '$1');

		// 5. 还原保护的 ai-link 标签
		protectedLinks.forEach((html, i) => {
			result = result.replace(`\x00AI_LINK_${i}\x00`, () => html);
		});

		return result;
	},

	//处理表格内图片 - 流式输出过程中，使用较小尺寸的行内占位符，避免破坏 <td> 布局
	handleTableImage(url) {
		if (!url) return url;

		// 生成唯一的ID用于占位符和图片关联
		const imageId = 'img_' + Math.random().toString(36).substr(2, 9);

		// 表格专用行内占位符（span+inline-flex）
		return `<span class="image-placeholder-table" id="placeholder_${imageId}" style="display:inline-flex;align-items:center;justify-content:center;width:120px;height:80px;margin:4px;padding:6px;background:#f5f5f5;border-radius:4px;vertical-align:middle;"><i class="el-icon-loading" style="font-size:16px;color:#999;"></i><img src="${url}" class="real-image" id="${imageId}" style="display:none;max-width:100%;max-height:100%;border-radius:3px;" /></span>`;
	},

	//处理图片 - 流式输出过程中，将图片URL替换为文字占位符
	handleImage(url) {
		if (!url) return url;

		// 生成唯一的ID用于占位符和图片关联
		const imageId = 'img_' + Math.random().toString(36).substr(2, 9);

		// 返回文字占位符HTML结构
		return `<div class="image-placeholder" id="placeholder_${imageId}" style="height: 200px; text-align: center; margin: 16px 0; padding: 20px; background: #f5f5f5; border-radius: 8px;">
			<p style="color: #999; margin: 0;height: 150px;display: flex;flex-direction: column;align-items: center;justify-content: center;"><i class="el-icon-loading" style="font-size: 24px;margin-bottom: 8px;"></i><span>图片解析中...</span></p>
			<img src="${url}" class="real-image" id="${imageId}" style="display: none; max-width: 100%; max-height: 200px; border-radius: 4px;" />
		</div>`;

		// onload="document.getElementById('placeholder_${imageId}').querySelector('p').style.display='none'; this.style.display='inline-block';"
	},

	//流式输出全部结束，将占位符替换为真实图片显示
	showRealImage(content) {
		if (!content) return content;

		// 1. 表格内行内占位符：<span class="image-placeholder-table">...</span>
		let result = content.replace(
			/<span class="image-placeholder-table"[^>]*>(\s*)<i[^>]*><\/i>(\s*)<img([^>]*?)src="([^"]+)"([^>]*?)class="real-image"([^>]*?)style="([^"]*?)display:\s*none;([^"]*)"([^>]*)>(\s*)<\/span>/gi,
			(match, spaceBefore1, spaceBefore2, imgAttrsBefore, realSrc, imgAttrsMiddle, imgAttrsAfter, styleBefore, styleAfter) => {
				const newStyle = `${styleBefore}display: inline-block;${styleAfter}`.trim();
				return `<span class="image-placeholder-table" style="display:inline-flex;align-items:center;justify-content:center;width:120px;height:80px;margin:4px;padding:4px;background:#f5f5f5;border-radius:4px;vertical-align:middle;cursor:pointer;" onclick="EReg.enlargeImage('${realSrc}')"><img${imgAttrsBefore}src="${realSrc}"${imgAttrsMiddle}class="real-image" title="点击放大" ${imgAttrsAfter}style="${newStyle} max-width:100%;max-height:100%;border-radius:3px;"${imgAttrsAfter}></span>`;
			}
		);

		// 2. 正常块级占位符：<div class="image-placeholder">...</div>（保留 p 去除，真实图片 display: block）
		return result.replace(
			/<div class="image-placeholder"[^>]*>(\s*)<p[^>]*>.*?<\/p>(\s*)<img([^>]*?)src="([^"]+)"([^>]*?)class="real-image"([^>]*?)style="([^"]*?)display:\s*none;([^"]*)"([^>]*)>(\s*)<\/div>/gi,
			(match, spaceBefore, spaceAfter, imgAttrsBefore, realSrc, imgAttrsMiddle, imgAttrsAfter, styleBefore, styleAfter) => {
				// 构建新的样式，将 display: none 替换为 display: block
				const newStyle = `${styleBefore}display: block;${styleAfter}`.trim();

				// 保留 div.image-placeholder 结构，只移除 p 标签，显示真实图片，添加点击放大事件
				return `<div class="image-placeholder" style="display: flex;align-items: center;height: 200px; text-align: center; margin: 16px 0; padding: 20px; background: #f5f5f5; border-radius: 8px; cursor: pointer;" onclick="EReg.enlargeImage('${realSrc}')">${spaceBefore}<img${imgAttrsBefore}src="${realSrc}"${imgAttrsMiddle}class="real-image" title="点击放大" ${imgAttrsAfter}style="${newStyle} max-width: 100%; max-height: 200px;margin: 0 auto;"${imgAttrsAfter}>${spaceAfter}</div>`;
			}
		);
		return this.removeImageAlt(result);
	},

	//点击某一个对话框的时候,图片和流式输出一样展示 样式 参考 showRealImage 方法中的
	showContentImage(content) {
		if (!content) return content;

		// 将普通图片转换为占位符样式展示，与流式输出保持一致
		// 匹配 markdown 图片格式 ![alt](src)
		let result = content.replace(/!\[([^\]]*)\]\(([^\)]+)\)/g, (match, alt, src) => {
			const imageId = 'img_' + Math.random().toString(36).substr(2, 9);
			return `<div class="image-placeholder" id="placeholder_${imageId}" style="display: flex;align-items: center;height: 200px; text-align: center; margin: 16px 0; padding: 20px; background: #f5f5f5; border-radius: 8px; cursor: pointer;" onclick="EReg.enlargeImage('${src}')">
			<img src="${src}" class="real-image" title="点击放大" id="${imageId}" alt="${alt || '图片'}" style="display: block; max-width: 100%; max-height: 200px; margin: 0 auto; border-radius: 4px;" />
		</div>`;
		});

		// 匹配 HTML img 标签（支持单引号和双引号）
		result = result.replace(/<img([^>]*?)src=["']([^"']+)["']([^>]*?)>/gi, (match, beforeSrc, src, afterSrc) => {
			// 匹配 alt 属性（支持单引号和双引号）
			const altMatch = match.match(/alt=["']([^"]*)["']/);
			const alt = altMatch ? altMatch[1] : '图片';
			const imageId = 'img_' + Math.random().toString(36).substr(2, 9);
			return `<div class="image-placeholder" id="placeholder_${imageId}" style="display: flex;align-items: center;height: 200px; text-align: center; margin: 16px 0; padding: 20px; background: #f5f5f5; border-radius: 8px; cursor: pointer;" onclick="EReg.enlargeImage('${src}')">
			<img src="${src}" class="real-image" title="点击放大" id="${imageId}" alt="${alt}" style="display: block; max-width: 100%; max-height: 200px;margin: 0 auto; border-radius: 4px;" />
		</div>`;
		});
		return this.removeImageAlt(result);
	},

	// 判断当前累积内容是否处于 markdown 表格上下文
	// markdown 表格特征：连续多行以 | 开头/结尾，且存在分隔符行（如 |---|---| 或 | :---: | --- |）
	isInTableContext(content) {
		if (!content) return false;
		// 取最后一个空行之后的内容（即当前所在段落，因为 markdown 表格遇到空行即结束）
		const lastBlankIdx = content.lastIndexOf('\n\n');
		const currentBlock = lastBlankIdx >= 0 ? content.slice(lastBlankIdx + 2) : content;
		// 1. 包含 markdown 表格分隔符行：|---|---| 或 | :---: | --- |
		const separatorReg = /(^|\n)\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*(\n|$)/;
		if (separatorReg.test(currentBlock)) return true;
		// 2. 最后两个非空行均以 | 开头（已开始接收表格行，分隔符尚未到达）
		const lines = currentBlock.split('\n').filter(l => l.trim().length);
		if (lines.length >= 2) {
			const lastLine = lines[lines.length - 1].trim();
			const prevLine = lines[lines.length - 2].trim();
			if (lastLine.startsWith('|') && prevLine.startsWith('|')) {
				return true;
			}
		}
		return false;
	},

	// 针对整段内容逐张图片判断是否位于 markdown 表格中：
	// 在表格中的图片走 handleTableImage（行内 img），不在表格中的仍交给 showContentImage 处理
	formatImageWithTableCheck(content) {
		if (!content) return content;
		const tableImageMap = {};
		let tokenIdx = 0;
		const genToken = () => `__TABLE_IMG_TOKEN_${tokenIdx++}__`;

		// 1. 处理 markdown 图片 ![alt](src)
		let result = content.replace(/!\[([^\]]*)\]\(([^\)]+)\)/g, (match, alt, src, offset) => {
			if (this.isInTableContext(content.slice(0, offset))) {
				const token = genToken();
				tableImageMap[token] = this.handleTableImage(src);
				return token;
			}
			return match;
		});

		// 2. 处理 HTML <img> 标签
		result = result.replace(/<img([^>]*?)src=["']([^"']+)["']([^>]*?)>/gi, (match, beforeSrc, src, afterSrc, offset) => {
			if (this.isInTableContext(result.slice(0, offset))) {
				const token = genToken();
				tableImageMap[token] = this.handleTableImage(src);
				return token;
			}
			return match;
		});

		// 3. 非表格图片交给 showContentImage 转为占位符块级展示
		result = this.showContentImage(result);

		// 4. 还原表格内图片占位符
		Object.keys(tableImageMap).forEach(token => {
			result = result.replace(token, tableImageMap[token]);
		});

		// 5. 历史对话不会再走 SSE end 收尾，这里直接把两类占位符（块级 + 表格行内）转为真实图片，避免一直 loading
		result = this.showRealImage(result);

		return result;
	},

	// 去除所有 img 标签的 alt 属性
	removeImageAlt(content) {
		if (!content) return content;
		return content.replace(/<img\s[^>]*?\balt\s*=\s*["'][^"']*["'][^>]*?>/gi, match => {
			return match.replace(/\s*\balt\s*=\s*["'][^"']*["']/gi, '');
		});
	},

	// 图片放大显示 - 挂载到 window 对象以便在 HTML 字符串中调用
	enlargeImage(imgElement) {
		// 如果传入的是字符串(URL)，则创建临时图片元素
		let img = imgElement;
		if (typeof imgElement === 'string') {
			img = document.createElement('img');
			img.src = imgElement;
			img.alt = '图片';
		}

		// 创建遮罩层
		const overlay = document.createElement('div');
		overlay.style.cssText = `
			position: fixed;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background: rgba(0, 0, 0, 0.8);
			display: flex;
			align-items: center;
			justify-content: center;
			z-index: 9999;
			overflow: hidden;
		`;

		// 创建关闭按钮
		const closeBtn = document.createElement('div');
		closeBtn.innerHTML = '\u00d7';
		closeBtn.style.cssText = `
			position: absolute;
			top: 20px;
			right: 30px;
			width: 40px;
			height: 40px;
			line-height: 40px;
			text-align: center;
			font-size: 30px;
			color: #fff;
			cursor: pointer;
			z-index: 10000;
			border-radius: 50%;
			background: rgba(255, 255, 255, 0.1);
			transition: background 0.3s ease;
		`;
		closeBtn.onmouseover = function () {
			this.style.background = 'rgba(255, 255, 255, 0.3)';
		};
		closeBtn.onmouseout = function () {
			this.style.background = 'rgba(255, 255, 255, 0.1)';
		};
		closeBtn.onclick = function (event) {
			event.stopPropagation();
			document.body.removeChild(overlay);
		};

		// 创建放大后的图片
		const enlargedImg = document.createElement('img');
		enlargedImg.src = img.src || imgElement;
		enlargedImg.alt = img.alt || '图片';
		enlargedImg.style.cssText = `
			max-width: 90%;
			max-height: 90%;
			border-radius: 8px;
			box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
			cursor: pointer;
			user-select: none;
			transform-origin: center center;
			will-change: transform;
		`;

		// 缩放 & 拖拽状态
		let scale = 1;
		let translateX = 0;
		let translateY = 0;
		let isDragging = false;
		let lastMouseX = 0;
		let lastMouseY = 0;
		let lastTranslateX = 0;
		let lastTranslateY = 0;
		const MIN_SCALE = 0.2;
		const MAX_SCALE = 5;
		const ZOOM_STEP = 0.15;

		const applyTransform = () => {
			enlargedImg.style.transform = `scale(${scale}) translate(${translateX}px, ${translateY}px)`;
		};

		const updateCursor = () => {
			enlargedImg.style.cursor = scale > 1 ? 'grab' : 'pointer';
		};

		// 鼠标滚轮缩放
		overlay.addEventListener('wheel', function (event) {
			event.preventDefault();
			event.stopPropagation();
			const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
			scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale + delta));
			// 缩放到不超出视口时重置偏移
			if (scale <= 1) {
				translateX = 0;
				translateY = 0;
			}
			enlargedImg.style.transition = 'transform 0.2s ease';
			applyTransform();
			updateCursor();
		}, { passive: false });

		// 图片上按下鼠标开始拖拽
		enlargedImg.addEventListener('mousedown', function (event) {
			if (scale <= 1) return;
			event.preventDefault();
			event.stopPropagation();
			isDragging = true;
			lastMouseX = event.clientX;
			lastMouseY = event.clientY;
			lastTranslateX = translateX;
			lastTranslateY = translateY;
			enlargedImg.style.transition = 'none';
			enlargedImg.style.cursor = 'grabbing';
		});

		// 全局鼠标移动拖拽（rAF 节流，每帧只写一次 DOM）
		let rafPending = false;
		const onMouseMove = function (event) {
			if (!isDragging) return;
			translateX = lastTranslateX + event.clientX - lastMouseX;
			translateY = lastTranslateY + event.clientY - lastMouseY;
			if (!rafPending) {
				rafPending = true;
				requestAnimationFrame(() => {
					rafPending = false;
					applyTransform();
				});
			}
		};

		// 全局鼠标松开
		const onMouseUp = function () {
			if (!isDragging) return;
			isDragging = false;
			enlargedImg.style.transition = 'transform 0.2s ease';
			updateCursor();
		};

		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp);

		// 关闭时清理全局事件
		const cleanup = () => {
			document.removeEventListener('mousemove', onMouseMove);
			document.removeEventListener('mouseup', onMouseUp);
		};
		const originalOverlayClick = overlay.onclick;

		// 点击遮罩层关闭
		overlay.onclick = function () {
			cleanup();
			document.body.removeChild(overlay);
		};

		// 点击关闭按钮关闭
		closeBtn.onclick = function (event) {
			event.stopPropagation();
			cleanup();
			document.body.removeChild(overlay);
		};

		// 点击图片不关闭（防止误触）
		enlargedImg.onclick = function (event) {
			event.stopPropagation();
		};

		overlay.appendChild(closeBtn);
		overlay.appendChild(enlargedImg);
		document.body.appendChild(overlay);
	},

	//table 表格 强制与上面 空一行，并修复空单元格导致的解析失败
	tableToNewLine(md) {
		// console.log('处理表格');
		const codeBlockPlaceholder = [];
		md = md.replace(/```[\s\S]*?```/g, m => {
			codeBlockPlaceholder.push(m);
			return `__CODE_${codeBlockPlaceholder.length - 1}__`;
		});

		// 修复空表格单元格：| | → | &nbsp; |（仅匹配同一行内，\s 改为 [ \t] 避免跨行）
		md = md.replace(/\|[ \t]*\|/g, match => {
			if (match === '||' || /^\|[ \t]+\|$/.test(match)) return '| &nbsp; |';
			return match;
		});

		// 严格匹配：表头行 + 下一行是 |---| 分隔线
		const NL = String.fromCharCode(10);
		const tableReg = new RegExp('([^' + NL + '])' + NL + '(?=\\|.*?\\|' + NL + '\\|[-: ]+\\|)', 'g');
		md = md.replace(tableReg, '$1' + NL + NL);

		md = md.replace(/__CODE_(\d+)__/g, (_, i) => codeBlockPlaceholder[i]);

		// 表格结束后紧跟的缩进独立 ai-link 行：剥离行首缩进，
		// 防止 4 空格缩进被 markdown 解析为代码块导致 HTML 被转义（页面直接显示原始 <a> 标签）
		// 列表项内的缩进引用不匹配此规则（其前面是列表项内容而不是表格行），不受影响
		const tableEndLinkReg = new RegExp('(\\|[^' + NL + ']*\\|[ \\t]*' + NL + '(?:[ \\t]*' + NL + ')*)([ \\t]+<a class="ai-link"[^' + NL + ']*?<\\/a>[ \\t]*)(?=' + NL + '|$)', 'g');
		md = md.replace(tableEndLinkReg, (match, tableEnd, linkLine) => tableEnd + linkLine.replace(/^[ \t]+/, ''));

		// 表格前紧邻的缩进加粗小标题行（4 空格缩进会被解析为代码块，显示原始 ** 文本）：剥离缩进
		const tableHeadBoldReg = new RegExp('(^|' + NL + ')[ \\t]+(\\*\\*[^' + NL + ']*\\*\\*)[ \\t]*(?=' + NL + '(?:[ \\t]*' + NL + ')*\\|[^' + NL + ']*\\|' + NL + '\\|[-: ]+\\|)', 'g');
		md = md.replace(tableHeadBoldReg, '$1$2');

		// 段落边界（空行后）的 4+ 空格缩进列表行：剥离行首缩进，并将列表标记 "- " 转义为字面文本，
		// 防止被 markdown 解析为代码块（整段变成 <pre><code>，内部 HTML 被转义），
		// 同时避免变成 <ul><li> 列表导致与段落内惰性延续列表行的渲染格式不一致（描述 + 链接同段）
		// 列表项内的嵌套子列表（前面是列表项内容行而非空行）不匹配，不受影响
		const blockListIndentReg = new RegExp('(' + NL + NL + ')[ \\t]{4,}(-[ \\t])', 'g');
		md = md.replace(blockListIndentReg, '$1\\$2');

		// console.log('处理表格',md);
		return md;
	},

	//去除LaTeX公式中的换行符
	removeNewlinesInLatexFormulas(content) {
		if (!content) return content;

		let result = content;

		// 1. 处理行间公式 $$...$$ 中的换行符
		result = result.replace(/\$\$([\s\S]*?)\$\$/g, (match, formula) => {
			const cleanedFormula = formula.replace(/\n/g, ' ').replace(/\r/g, ' ').replace(/\t/g, ' ');
			return '$$' + cleanedFormula + '$$';
		});

		// 2. 处理行间公式 \\[...\\] 中的换行符
		result = result.replace(/\\\[([\s\S]*?)\\\]/g, (match, formula) => {
			const cleanedFormula = formula.replace(/\n/g, ' ').replace(/\r/g, ' ').replace(/\t/g, ' ');
			return '\\[' + cleanedFormula + '\\]';
		});

		return result;
	},

	// KaTeX 0.16 用内联 SVG 绘制根号等拉伸符号，SVG 内部含换行符；
	// markdown 表格行不允许跨行，表格单元格内的多行 SVG 会打断表格解析，
	// 导致 path 数据泄露为可见文本（如 "M834 80h400000v40h-400000z"），这里移除 SVG 内部换行
	flattenKatexSvg(html) {
		if (!html || html.indexOf('<svg') === -1) return html;
		return html.replace(/<svg[^>]*>[\s\S]*?<\/svg>/g, svg => svg.replace(/\n/g, ''));
	},

	// 智能LaTeX公式渲染：只在有完整结尾符号时才渲染
	smartRenderLatexFormulas(content) {
		if (!content) return content;

		let result = content;

		// KaTeX 渲染辅助：渲染并移除 annotation 元素，防止 vue-markdown/markdown-it 解析 MathML 时破坏 HTML
		const renderKatex = (formula, opts) => {
			const html = Katex.renderToString(formula, { throwOnError: false, ...opts });
			// 移除 <annotation>：避免 markdown-it 将内部 LaTeX 源码中的 _、[、] 误解析为 markdown 语法
			return EReg.flattenKatexSvg(html.replace(/<annotation[^>]*>[\s\S]*?<\/annotation>/g, ''));
		};

		// 1. 渲染 $$...$$ 行间公式   去掉 $$  $$  中 前后的空格
		result = result.replace(/\$\$([\s\S]*?)\$\$/g, (match, formula) => {
			try {
				const trimmed = formula.trim();
				if (!trimmed) return match;
				return renderKatex(trimmed, { displayMode: true });
			} catch (error) {
				console.warn('LaTeX $$渲染错误:', error, formula);
				return match;
			}
		});

		// 2. 渲染 \[...\] 行间公式
		result = result.replace(/\\\[([\s\S]*?)\\\]/g, (match, formula) => {
			try {
				const trimmed = formula.trim();
				if (!trimmed) return match;
				return renderKatex(trimmed, { displayMode: true });
			} catch (error) {
				console.warn('LaTeX \\[渲染错误:', error, formula);
				return match;
			}
		});

		// 3. 渲染 $...$ 行内公式（零宽断言避免与 $$...$$ 冲突，且支持连续公式如 $a$ $b$）
		result = result.replace(/(?<!\$)\$([^$\n\r]+)\$(?!\$)/g, (match, formula) => {
			try {
				return renderKatex(formula.trim(), { displayMode: false });
			} catch (error) {
				console.warn('LaTeX $渲染错误:', error, formula);
				return match;
			}
		});

		// 4. 渲染 \(...\) 行内公式
		result = result.replace(/\\\(([\s\S]*?)\\\)/g, (match, formula) => {
			try {
				const trimmed = formula.trim();
				if (!trimmed) return match;
				return renderKatex(trimmed, { displayMode: false });
			} catch (error) {
				console.warn('LaTeX \\(渲染错误:', error, formula);
				return match;
			}
		});

		return result;
	},

	//格式化latex表达式
	renderedFormula(stext) {
		const patterns = [
			{ regex: /\$\$(.*?)\$\$/g, displayMode: true }, // $$...$$
			{ regex: /\$(.*?)\$/g, displayMode: false }, // $...$
			{ regex: /\\\[(.*?)\\\]/g, displayMode: true }, // \[...\]
			{ regex: /\\\((.*?)\\\)/g, displayMode: false }, // \(...\)
			{ regex: /\\\\[(.*?)\\\\]/g, displayMode: true } // \\[...\\]
		];

		// console.log('stext', stext);

		let result = stext;
		result = result.replace('&lt;', '<');
		result = result.replace('&gt;', '>');
		// match = match.replace('&le;', '\\le');
		// match = match.replace('&ge;', '\\ge');

		patterns.forEach(({ regex, displayMode }) => {
			result = result.replace(regex, (match, formula) => {
				// console.log('formula---', formula, match);

				try {
					return EReg.flattenKatexSvg(Katex.renderToString(formula, {
						throwOnError: false,
						displayMode
					}));
				} catch (error) {
					return `<span class="katex-error">${match}</span>`;
				}
			});
		});

		// console.log('result', result);

		return result;
	},

	// 去掉前后的空格
	trim(str) {
		if (typeof str == 'string') {
			return str.replace(/^\s*|\s*$/g, '');
		} else {
			return str;
		}
	},

	// 获得文件的后缀名
	getExt(filename) {
		if (!filename || filename.lastIndexOf('.') === -1 || filename.lastIndexOf('.') === filename.length - 1) {
			return '';
		}

		return filename.slice(filename.lastIndexOf('.') + 1).toLowerCase();
	},

	// 是否是空
	isEmpty(str) {
		switch (typeof str) {
			case 'string':
				return EReg.trim(str).length == 0;
				break;

			case 'number':
				return str == 0;
				break;

			case 'object':
				return str == null;
				break;

			case 'array':
				return str.length == 0;
				break;

			default:
				return true;
		}
	},

	// 是否是整数类型
	isInt(txt, len) {
		let reg = /^[0-9]+$/;
		len = len ? parseInt(len) : 0;

		if (len > 0) {
			return !!(txt.length == len && reg.test(txt));
		} else {
			return reg.test(txt);
		}
	},

	// 整数或者 整数.5
	isHalfInt(txt) {
		let reg = /^[0-9]+(\.5)?$/;

		return reg.test(txt);
	},

	// 是否是数字
	isNumber(txt) {
		let reg1 = /^[0-9]+[\.|,]?(?:[0-9]+)?$/;

		return reg1.test(txt);
	},

	// 数字或者两位小数的数字
	isNumer2(txt) {
		let reg = /^[\d]+(\.[0-9]{1,2})?$/;

		return reg.test(txt);
	},

	// 密码是否合法
	isPassword(txt) {
		txt = EReg.trim(txt);

		if (txt.length < 6) {
			return false;
		}

		return true;
	},

	/**
	 * 判断是否是手机号（宽松）
	 *
	 * @说明：1开头，11位数字
	 * ====================================
	 * @param   mobile  string    传入电话号码
	 * @return  int  true:符合  false：不符合
	 */
	isSimMobile(num) {
		let reg = /^1\d{10}$/;

		return reg.test(num);
	},

	/**
	 * 判断Email地址是否合法
	 *
	 * @param   txt     要验证的email字符串
	 */
	isEmail(email) {
		var reg1 = /([\w-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)/;
		return reg1.test(email);
	}
};

// 将 enlargeImage 方法挂载到 window 对象，以便在 HTML 字符串中通过 onclick 调用
if (typeof window !== 'undefined') {
	window.EReg = EReg;
}

export default EReg;
